import { useState } from "react";
import { Link } from "react-router-dom";
import { Badge } from "../components/ui/Badge";
import { Button } from "../components/ui/Button";
import { Card, CardBody } from "../components/ui/Card";
import { EmptyState } from "../components/ui/EmptyState";
import { ErrorState } from "../components/ui/ErrorState";
import { BriefingsIcon, ChevronLeftIcon, ChevronRightIcon } from "../components/ui/icons";
import { SkeletonScreen } from "../components/ui/Skeleton";
import { briefingsList, briefingView } from "../copy/sq";
import { formatDate, formatDateTime } from "../lib/date";
import { useBriefings } from "../hooks/useApi";

const PAGE_SIZE = 14;

export function BriefingsScreen() {
  const [offset, setOffset] = useState(0);
  const { data, isLoading, isError, error, refetch } = useBriefings(PAGE_SIZE, offset);

  if (isLoading) return <SkeletonScreen cards={4} />;

  if (isError) {
    return <ErrorState title={briefingsList.errorTitle} message={error instanceof Error ? error.message : undefined} onRetry={() => refetch()} />;
  }

  if (!data || data.items.length === 0) {
    return (
      <EmptyState
        icon={<BriefingsIcon width={32} height={32} />}
        title={briefingsList.emptyTitle}
        description={briefingsList.emptyDescription}
      />
    );
  }

  const hasNext = offset + data.items.length < data.total;
  const hasPrev = offset > 0;

  return (
    <div className="space-y-3">
      {data.items.map((briefing) => (
        <Link key={briefing.id} to={`/briefings/${briefing.id}`} className="block">
          <Card className="transition-shadow hover:shadow-card-hover">
            <CardBody className="flex flex-wrap items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">{formatDate(briefing.briefing_date)}</p>
                <p className="mt-0.5 truncate font-serif text-base font-semibold text-ink-950">{briefing.title ?? briefingsList.untitled}</p>
                <p className="mt-1 text-xs text-ink-400">
                  {briefingsList.generatedAt(formatDateTime(briefing.generated_at))} · {briefingsList.sectionsCount(briefing.section_count)} ·{" "}
                  {briefingsList.topicsCount(briefing.topic_count)}
                </p>
              </div>
              <Badge tone={briefing.delivered ? "good" : "neutral"}>{briefing.delivered ? briefingView.delivered : briefingView.notDelivered}</Badge>
            </CardBody>
          </Card>
        </Link>
      ))}

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
        <p className="text-xs text-ink-400">{briefingsList.rangeOfTotal(offset + 1, offset + data.items.length, data.total)}</p>
        <div className="flex gap-2">
          <Button variant="secondary" disabled={!hasPrev} onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}>
            <ChevronLeftIcon width={16} height={16} /> {briefingsList.newer}
          </Button>
          <Button variant="secondary" disabled={!hasNext} onClick={() => setOffset((o) => o + PAGE_SIZE)}>
            {briefingsList.older} <ChevronRightIcon width={16} height={16} />
          </Button>
        </div>
      </div>
    </div>
  );
}
